const ALLOWED_ACTION_TYPES = new Set([
  "set_project_material",
  "update_node_parameters",
  "update_node_transform",
  "set_front_layout",
  "set_cabinet_role",
  "update_generated_panel",
  "add_cabinet_next_to_selected",
  "delete_selected",
  "ask_clarification",
  "update_nodes_by_filter",
]);

const ALLOWED_TARGETS = new Set([
  "selected",
  "simpleSelected",
  "project",
  "scene",
  undefined,
  null,
  "",
]);

const ALLOWED_FRONT_TYPES = new Set([
  "open",
  "doors",
  "drawers",
  "none",
  undefined,
  null,
  "",
]);

const ALLOWED_DIRECTIONS = new Set([
  "left",
  "right",
  "front",
  "back",
  undefined,
  null,
  "",
]);

const ALLOWED_PROJECT_MATERIAL_KEYS = new Set([
  "corpusBoardMaterialId",
  "corpusEdgeMaterialId",
  "frontBoardMaterialId",
  "frontEdgeMaterialId",
  "backBoardMaterialId",
  "backEdgeMaterialId",
  "shelfBoardMaterialId",
  "shelfEdgeMaterialId",
]);

const ALLOWED_PARAMETER_KEYS = new Set([
  "width",
  "height",
  "depth",
  "floorOffset",
  "shelfCount",
  "shelfThickness",
  "frontGapOuter",
  "frontGapInner",
  "doorCount",
  "drawerCount",
  "frontType",
  "cabinetRole",
  "applianceType",
]);

const PARAMETER_LIMITS = {
  width: { min: 50, max: 3000 },
  height: { min: 50, max: 3000 },
  depth: { min: 50, max: 1500 },
  floorOffset: { min: -500, max: 3000 },
  shelfCount: { min: 0, max: 20 },
  shelfThickness: { min: 1, max: 80 },
  frontGapOuter: { min: 0, max: 50 },
  frontGapInner: { min: 0, max: 50 },
  doorCount: { min: 0, max: 10 },
  drawerCount: { min: 0, max: 10 },
};

function isNumberLike(value) {
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value !== "string") return false;
  if (!value.trim()) return false;
  return Number.isFinite(Number(value));
}

function validateNumberRange({ key, value, index, errors, warnings }) {
  const limits = PARAMETER_LIMITS[key];

  if (!limits) return;

  if (!isNumberLike(value)) {
    errors.push({
      index,
      field: key,
      message: `Parameter "${key}" must be a number.`,
    });
    return;
  }

  const n = Number(value);

  if (n < limits.min || n > limits.max) {
    errors.push({
      index,
      field: key,
      message: `Parameter "${key}" is out of range. Allowed ${limits.min}–${limits.max}.`,
    });
  }

  if (key === "width" && n % 10 !== 0) {
    warnings.push({
      index,
      field: key,
      message: `Width ${n} is not rounded to 10mm.`,
    });
  }
}

function getTargetExists({ action, selectedNode, simpleSelectedNode }) {
  if (action.target === "project" || action.target === "scene") {
    return true;
  }

  if (action.target === "selected") {
    return Boolean(selectedNode || simpleSelectedNode);
  }

  if (action.target === "simpleSelected") {
    return Boolean(simpleSelectedNode || selectedNode);
  }

  return Boolean(simpleSelectedNode || selectedNode);
}

function validateParameterOverrides(action, index, errors, warnings) {
  const overrides = action.parameterOverrides || {};

  Object.entries(overrides).forEach(([key, value]) => {
    if (!ALLOWED_PARAMETER_KEYS.has(key)) {
      errors.push({
        index,
        field: key,
        message: `Parameter "${key}" is not allowed for AI edit.`,
      });
      return;
    }

    validateNumberRange({
      key,
      value,
      index,
      errors,
      warnings,
    });

    if (key === "frontType" && !ALLOWED_FRONT_TYPES.has(value)) {
      errors.push({
        index,
        field: key,
        message: `frontType "${value}" is not allowed.`,
      });
    }
  });
}

function validateAction(action, index, context, errors, warnings) {
  if (!action || typeof action !== "object") {
    errors.push({
      index,
      message: "Action must be an object.",
    });
    return;
  }

  if (!ALLOWED_ACTION_TYPES.has(action.type)) {
    errors.push({
      index,
      field: "type",
      message: `Action type "${action.type}" is not allowed.`,
    });
    return;
  }

  if (!ALLOWED_TARGETS.has(action.target)) {
    errors.push({
      index,
      field: "target",
      message: `Target "${action.target}" is not allowed.`,
    });
  }

  if (!getTargetExists({
    action,
    selectedNode: context.selectedNode,
    simpleSelectedNode: context.simpleSelectedNode,
  })) {
    errors.push({
      index,
      field: "target",
      message: `Target "${action.target || "default"}" does not exist in current selection.`,
    });
  }

  if (action.type === "set_project_material") {
    if (!ALLOWED_PROJECT_MATERIAL_KEYS.has(action.key)) {
      errors.push({
        index,
        field: "key",
        message: `Project material key "${action.key}" is not allowed.`,
      });
    }

    if (!isNumberLike(action.materialId)) {
      errors.push({
        index,
        field: "materialId",
        message: "materialId must be a number.",
      });
    }

    return;
  }

  if (action.type === "update_node_parameters") {
    if (!action.parameterOverrides || typeof action.parameterOverrides !== "object") {
      errors.push({
        index,
        field: "parameterOverrides",
        message: "update_node_parameters requires parameterOverrides.",
      });
      return;
    }

    validateParameterOverrides(action, index, errors, warnings);
    return;
  }

  if (action.type === "update_nodes_by_filter") {
  if (action.target !== "scene") {
    errors.push({
      index,
      field: "target",
      message: "update_nodes_by_filter must use target \"scene\".",
    });
  }

  if (!action.filter || typeof action.filter !== "object") {
    errors.push({
      index,
      field: "filter",
      message: "update_nodes_by_filter requires filter object.",
    });
  }

  if (!action.parameterOverrides || typeof action.parameterOverrides !== "object") {
    errors.push({
      index,
      field: "parameterOverrides",
      message: "update_nodes_by_filter requires parameterOverrides.",
    });
    return;
  }

  validateParameterOverrides(action, index, errors, warnings);
  return;
}

  if (action.type === "set_front_layout") {
    if (!ALLOWED_FRONT_TYPES.has(action.frontType)) {
      errors.push({
        index,
        field: "frontType",
        message: `frontType "${action.frontType}" is not allowed.`,
      });
    }

    if (action.frontType === "drawers") {
      validateNumberRange({
        key: "drawerCount",
        value: action.drawerCount ?? 3,
        index,
        errors,
        warnings,
      });
    }

    if (action.frontType === "doors") {
      validateNumberRange({
        key: "doorCount",
        value: action.doorCount ?? 1,
        index,
        errors,
        warnings,
      });
    }

    return;
  }

  if (action.type === "set_cabinet_role") {
    if (!action.cabinetRole) {
      warnings.push({
        index,
        field: "cabinetRole",
        message: "cabinetRole is empty; default may be used.",
      });
    }

    if (action.frontType && !ALLOWED_FRONT_TYPES.has(action.frontType)) {
      errors.push({
        index,
        field: "frontType",
        message: `frontType "${action.frontType}" is not allowed.`,
      });
    }

    return;
  }

  if (action.type === "add_cabinet_next_to_selected") {
    if (!ALLOWED_DIRECTIONS.has(action.direction)) {
      errors.push({
        index,
        field: "direction",
        message: `Direction "${action.direction}" is not allowed.`,
      });
    }

    return;
  }

  if (action.type === "update_node_transform") {
    if (!action.transform || typeof action.transform !== "object") {
      errors.push({
        index,
        field: "transform",
        message: "update_node_transform requires transform object.",
      });
    }

    return;
  }

  if (action.type === "update_generated_panel") {
    if (!context.resolvedNode?.parentGeneratedFromId) {
      errors.push({
        index,
        field: "target",
        message: "update_generated_panel requires selected generated panel/board.",
      });
    }

    return;
  }
}

export function validateSceneEditActions({
  editResult,
  selectedNode,
  resolvedNode,
  simpleSelectedNode,
  simpleResolvedNode,
}) {
  const errors = [];
  const warnings = [];

  if (!editResult || typeof editResult !== "object") {
    return {
      ok: false,
      errors: [
        {
          message: "AI edit result is empty or invalid.",
        },
      ],
      warnings,
    };
  }

  if (editResult.status === "needs_clarification") {
    return {
      ok: false,
      needsClarification: true,
      questions: editResult.questions || [],
      errors: [],
      warnings,
    };
  }

  if (editResult.status !== "ready_to_apply") {
    errors.push({
      field: "status",
      message: `Unsupported edit result status "${editResult.status}".`,
    });
  }

  const actions = Array.isArray(editResult.actions)
    ? editResult.actions
    : [];

  if (!actions.length) {
    errors.push({
      field: "actions",
      message: "No actions returned by AI.",
    });
  }

  actions.forEach((action, index) => {
    validateAction(
      action,
      index,
      {
        selectedNode,
        resolvedNode,
        simpleSelectedNode,
        simpleResolvedNode,
      },
      errors,
      warnings
    );
  });

  return {
    ok: errors.length === 0,
    errors,
    warnings,
    needsClarification: false,
    questions: [],
  };
}